<template>
  <div
    :class="[
      'vc-product-price',
      {
        'vc-product-price--align--start': align === 'start',
        'vc-product-price--align--end': align === 'end',
        'vc-product-price--single-line': singleLine,
        'vc-product-price--truncate': truncate,
        'vc-product-price--disabled': disabled,
      },
    ]"
  >
    <span class="vc-product-price__actual flex flex-col">
      <!-- Show 'From' text when there are multiple variations -->
      <span v-if="showFromLabel" class="vc-product-price__variations text-base">
        {{ $t("ui_kit.suffixes.from") }}
      </span>

      <!-- Show original price per unit amount (striped) when there are no variations and there is a reduction and the price is not 0 -->
      <InnoPriceDisplay
        v-if="showStripedPrice"
        class="vc-product-price__list"
        :value="listPrice"
        :show-currency-symbol="showCurrencySymbol"
      />

      <!-- The price you pay per unit amount -->
      <InnoPriceDisplay
        v-if="(unitPrice && unitPrice.amount > 0) || zeroPriceDisplay === 'zero'"
        :value="unitPrice"
        :show-currency-symbol="showCurrencySymbol"
        class="whitespace-nowrap"
      />
      <div v-else-if="zeroPriceDisplay === 'on-request'" class="price-on-request">
        {{ $t("ui_kit.labels.price-on-request") }}
      </div>
      <div v-else-if="zeroPriceDisplay === 'n/a'" class="price-on-request">
        {{ $t("ui_kit.labels.not_available") }}
      </div>

      <!-- Show the unit text. If there are variants and a starting price is shown (withFromLabel), we do not have the price unit value associated with the price shown -->
      <span v-if="priceUnitText" class="vc-product-price__price-unit"> {{ priceUnitText }}</span>

      <span v-if="salesUnitText" class="vc-product-price__sales-unit">{{ salesUnitText }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { shouldUseActualPrice } from "@/ui-kit/utilities/price";
import InnoPriceDisplay from "@/ui-kit/components/atoms/innovadis/inno-price-display.vue";
import type { MoneyType } from "@/core/api/graphql/types";

interface IProps {
  actualPrice?: MoneyType;
  listPrice?: MoneyType;
  align?: "start" | "end";
  singleLine?: boolean;
  truncate?: boolean;
  disabled?: boolean;
  withFromLabel?: boolean;
  priceUnitText?: string;
  salesUnitText?: string;
  showCurrencySymbol?: boolean;
  zeroPriceDisplay?: string;
}

const props = withDefaults(defineProps<IProps>(), {
  showCurrencySymbol: false,
  zeroPriceDisplay: "on-request",
});

const showFromLabel = computed(() => props.withFromLabel && props.actualPrice && props.actualPrice.amount > 0);

const showStripedPrice = computed(
  () =>
    !(props.withFromLabel && props.actualPrice && props.actualPrice.amount > 0) &&
    shouldUseActualPrice(props.listPrice, props.actualPrice) &&
    props.listPrice?.amount &&
    props.listPrice.amount > 0,
);

const unitPrice = computed(() => {
  // when has variants we show the product.minVariationPrice?.actual which is passed via props.actualPrice
  if (showFromLabel) {
    // when not signed in use this value
    return props.actualPrice;
  }

  // when there is no list price (or list price is 0), or the actual price is lowest, show actual price
  return shouldUseActualPrice(props.listPrice, props.actualPrice) || !props.listPrice
    ? props.actualPrice
    : props.listPrice;
});
</script>

<style lang="scss">
.vc-product-price {
  $self: &;
  $truncate: "";
  $variations: "";
  $singleLine: "";

  --font-size: var(--vc-product-price-font-size);

  @apply flex flex-col text-[length:var(--font-size)] text-neutral-950 [word-break:break-word] leading-[1.335];

  &--align {
    &--start {
      @apply justify-start text-start;
    }

    &--end {
      @apply justify-end text-end;
    }
  }

  &--single-line {
    $singleLine: &;

    @apply flex-row flex-wrap items-center gap-x-1;
  }

  &--truncate {
    $truncate: &;
  }

  &--disabled {
    @apply text-neutral;
  }

  &__actual {
    @apply font-bold;

    #{$truncate} & {
      @apply whitespace-nowrap max-w-full truncate;
    }
  }

  &__variations {
    $variations: &;

    @apply block whitespace-nowrap text-base font-normal text-[--color-body-text] m-0 !important;

    #{$singleLine} & {
      @apply inline-block me-1;
    }
  }

  &__unit {
    $variations: &;

    @apply block whitespace-nowrap w-full text-base font-normal text-[--color-body-text] m-0 !important;

    #{$singleLine} & {
      @apply inline-block me-1;
    }
  }

  &__list {
    @apply line-through text-neutral text-base !important;

    #{$truncate} & {
      @apply max-w-full truncate;
    }
  }

  @at-root .vc-product-card {
    #{$self} {
      grid-area: price;
    }

    &--view-mode {
      &--grid #{$self} {
        --font-size: theme("fontSize.lg");

        @apply mt-3 order-6;
      }

      &--list {
        #{$self} {
          --font-size: theme("fontSize.lg");

          @container (max-width: theme("containers.xl")) {
            @apply self-start mt-1 flex-row items-center gap-x-1.5 flex-wrap;
          }

          @container (min-width: theme("containers.xl")) {
            --font-size: theme("fontSize.sm");

            @apply ms-3 w-[7.5rem] text-end;
          }

          @container (min-width: theme("containers.4xl")) {
            --font-size: theme("fontSize.lg");

            @apply w-[9.5rem];
          }
        }

        #{$variations} {
          @apply inline-block me-1;

          @container (min-width: theme("containers.xl")) {
            @apply block;
          }
        }
      }

      &--item {
        #{$self} {
          @apply hidden;

          @container (min-width: theme("containers.4xl")) {
            --font-size: theme("fontSize.sm");

            @apply flex flex-col justify-end w-[6.75rem] text-end;
          }
        }

        .vc-product-total #{$self} {
          --font-size: theme("fontSize.base");

          @apply flex;
        }
      }
    }
  }
}
</style>
